/**
 * The comment composer grows from 44px to 178px when it is focused, and the newest
 * comment ends up hidden underneath it. The cause is different either side of the
 * 768px breakpoint, which is why this file handles two things rather than one.
 *
 * Desktop: the composer is a flex sibling, so the conversation pane genuinely gives
 * up the 134px and the two stay flush - measured overlap 0px. What goes wrong is that
 * the pane keeps its scroll position while it shrinks, so a reader sitting at the
 * bottom loses the last 134px of the conversation off the bottom edge.
 *
 * Mobile: the composer is `position: fixed`, so it floats over the pane instead of
 * taking space from it. The pane never resizes and nothing above applies. Measured at
 * 500px, a focused composer covers 252px of the pane and hides ~150px of the last
 * comment. That needs space reserved underneath the conversation instead.
 *
 * ResizeObserver fires after layout, by which point clientHeight has already shrunk,
 * so "was the reader at the bottom" cannot be measured - it has to be reconstructed
 * from how much height the pane just lost.
 *
 * Growth is deliberately left alone. When the composer collapses the pane gains space
 * and simply reveals more of the conversation, which needs no correction, and
 * re-pinning there would yank a reader out of the history they had scrolled back to.
 */

/** Matches the threshold `renderActivityData` already uses to decide the same question. */
export const NEAR_BOTTOM_PX = 100;

export type ScrollMetrics = {
  scrollHeight: number;
  scrollTop: number;
  clientHeight: number;
};

/**
 * `metrics` are read *after* the resize; `previousClientHeight` is what the pane
 * measured before it. Resizing does not reflow the content, because only the height
 * changes, so scrollHeight is the same on both sides of the change.
 */
export const shouldPinToBottom = (
  metrics: ScrollMetrics,
  previousClientHeight: number,
  threshold: number = NEAR_BOTTOM_PX
): boolean => {
  const lost = previousClientHeight - metrics.clientHeight;
  if (lost <= 0) return false;

  const distanceFromBottomAfter = metrics.scrollHeight - metrics.scrollTop - metrics.clientHeight;

  return distanceFromBottomAfter - lost < threshold;
};

export const isAtBottom = (metrics: ScrollMetrics, threshold: number = NEAR_BOTTOM_PX): boolean =>
  metrics.scrollHeight - metrics.scrollTop - metrics.clientHeight < threshold;

/**
 * How much bottom padding the pane needs so the conversation can scroll clear of a
 * composer that overlays it. `null` means "leave the stylesheet's own padding alone" -
 * the pane already reserves 80px at mobile (`pb-20`), which is why a collapsed 44px
 * composer never looked wrong.
 */
export const overlayReservation = (
  composerHeight: number,
  overlays: boolean,
  basePaddingPx: number
): number | null => {
  if (!overlays) return null;
  if (composerHeight <= basePaddingPx) return null;

  return composerHeight;
};

/**
 * Observes both elements, because only one of them changes size on any given
 * breakpoint: the pane on desktop, the composer on mobile.
 *
 * Both are watched border-box so that writing `paddingBottom` below - which changes
 * the pane's content box and nothing else - cannot feed back in as another resize.
 *
 * Returns the teardown, so an effect can hand it straight back to React.
 */
export const observeConversationComposer = (
  scrollEl: HTMLElement | null,
  composerEl: HTMLElement | null
): (() => void) => {
  if (!scrollEl || typeof ResizeObserver === 'undefined') return () => undefined;

  let previousClientHeight = scrollEl.clientHeight;
  let basePadding = parseFloat(getComputedStyle(scrollEl).paddingBottom) || 0;

  const sync = () => {
    const metrics: ScrollMetrics = {
      scrollHeight: scrollEl.scrollHeight,
      scrollTop: scrollEl.scrollTop,
      clientHeight: scrollEl.clientHeight,
    };

    const followTheBottom = isAtBottom(metrics) || shouldPinToBottom(metrics, previousClientHeight);

    if (composerEl) {
      // Only readable while nothing of ours is set on it, and it changes at the breakpoint.
      if (!scrollEl.style.paddingBottom) {
        basePadding = parseFloat(getComputedStyle(scrollEl).paddingBottom) || 0;
      }

      const reservation = overlayReservation(
        composerEl.getBoundingClientRect().height,
        getComputedStyle(composerEl).position === 'fixed',
        basePadding
      );
      const next = reservation === null ? '' : `${reservation}px`;

      if (scrollEl.style.paddingBottom !== next) scrollEl.style.paddingBottom = next;
    }

    if (followTheBottom) scrollEl.scrollTop = scrollEl.scrollHeight;

    previousClientHeight = scrollEl.clientHeight;
  };

  const observer = new ResizeObserver(sync);
  observer.observe(scrollEl, { box: 'border-box' });
  if (composerEl) observer.observe(composerEl, { box: 'border-box' });

  return () => observer.disconnect();
};
